import { api_configuration } from "../../common";
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_widget = api_configuration.libraries.gui_definition_lib.types.widget.alan_api;
import api_modification = api_configuration.types.modifications.alan_api;
import csr = api_configuration.types.client_state.alan_api;
import { PropertiesBinding, CollectionPropertyBinding, StateGroupBinding } from "./ClientBinderTypes";
import { isDefined } from "../validation";
import { NumberConverter } from "./number_converter";
import DestructorTree from "../DestructorTree";

type ApiNode = csr.AlanObject & {
	properties: Record<string, unknown>;
};
export type WidgetConfigurationNode =
	| api_views.Cgui_widget_configuration_node
	| api_modification.Cconfiguration_node
	| api_widget.Cwidget_configuration_node;
export default class ImplementationVisitorContext {
	private _explicit_context: ImplementationVisitorContext | undefined;

	constructor(
		explicit_context: ImplementationVisitorContext | undefined,
		public readonly widget_configuration: WidgetConfigurationNode,
		private _model_binding: api_views.Cmodel_binding | undefined,
		// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
		private _client_state_binder: any,
		// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
		private _current_binding_context: any,
		private _parent_widget_implementation_context: ImplementationVisitorContext | undefined,
		private _view_root: () => HTMLElement,
		private _client_view: csr.Copen_views,
		private _view_context_node: csr.Cnode,
		private _entry_keys: string[],
		public readonly last_gui_widget_configuration: api_views.Cgui_widget_configuration_node,
		public readonly number_converter: NumberConverter | undefined,
		public id_generator = createIdGenerator()
	) {
		if (_client_state_binder === undefined) {
			throw new Error("parameter client state binder can not be undefined");
		}
		this._explicit_context = explicit_context;
	}

	static create(
		// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
		binding_context: any,
		gui_widget_configuration: api_views.Cgui_widget_configuration_node,
		gui_model_binding: api_views.Cmodel_binding | undefined,
		// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
		client_state_binder: any,
		view_root: () => HTMLElement,
		client_view: csr.Copen_views,
		entry_keys: string[]
	): ImplementationVisitorContext {
		return new ImplementationVisitorContext(
			undefined,
			gui_widget_configuration,
			gui_model_binding,
			client_state_binder,
			binding_context,
			undefined,
			view_root,
			client_view,
			client_view.properties.view_context.output.result_node(),
			entry_keys.slice(),
			gui_widget_configuration,
			undefined,
			undefined
		);
	}

	get explicit_context(): ImplementationVisitorContext {
		return this._explicit_context ?? this;
	}

	get current_binding_context(): ApiNode {
		return this._current_binding_context;
	}

	get clientStateAsNode(): csr.Cnode {
		if (!(this._current_binding_context instanceof csr.Cnode)) {
			throw new Error("Expected client state to be of type Cnode");
		}
		return this._current_binding_context;
	}

	get gui_widget_configuration(): api_views.Cgui_widget_configuration_node {
		return this.widget_configuration as api_views.Cgui_widget_configuration_node;
	}

	get model_binding(): api_views.Cmodel_binding {
		return isDefined(this._model_binding);
	}

	get raw_model_binding(): api_views.Cmodel_binding | undefined {
		return this._model_binding;
	}

	get client_state_binder_AsPropertiesBinding(): PropertiesBinding {
		return this._client_state_binder;
	}

	get client_state_binder(): any {
		return this._client_state_binder;
	}

	get entry_keys(): string[] {
		return this._entry_keys;
	}

	get_view_root(): () => HTMLElement {
		return this._view_root;
	}

	get_client_view(): csr.Copen_views {
		return this._client_view;
	}

	get view_context_node(): csr.Cnode {
		return this._view_context_node;
	}

	get_client_state_binder_AsStateGroupBinding(property: string): StateGroupBinding {
		if (this._client_state_binder.properties[property] === undefined) {
			throw new Error("Property '" + property + "' does not exist");
		}
		return this._client_state_binder.properties[property];
	}

	get_client_state_binder_AsCollectionBinding(property: string): CollectionPropertyBinding {
		if (this._client_state_binder.properties[property] === undefined) {
			throw new Error("Property '" + property + "' does not exist");
		}
		return this._client_state_binder.properties[property];
	}

	get parent_widget_implementation_context(): ImplementationVisitorContext {
		return isDefined(this._parent_widget_implementation_context);
	}

	get root_widget_implementation_context(): ImplementationVisitorContext {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		let candidate: ImplementationVisitorContext = this;
		while (candidate._parent_widget_implementation_context !== undefined) {
			candidate = candidate._parent_widget_implementation_context;
		}
		return candidate;
	}

	switchGuiWidgetConfiguration(new_gui_widget_configuration: WidgetConfigurationNode): ImplementationVisitorContext {
		return this.changeState({ new_widget_configuration: new_gui_widget_configuration });
	}

	switchGuiModelBinding(new_gui_model_binding: api_views.Cmodel_binding): ImplementationVisitorContext {
		return this.changeState({
			new_model_binding: new_gui_model_binding,
		});
	}

	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	switchBindingContext(new_binding_context: any): ImplementationVisitorContext {
		if (new_binding_context === this._current_binding_context) {
			return this;
		} else {
			return this.changeState({
				new_binding_context: new_binding_context,
			});
		}
	}

	switchClientStateBinderToProperty(property_name: string, destructor: DestructorTree): ImplementationVisitorContext {
		const property_binding = (this._client_state_binder as PropertiesBinding).properties[property_name];
		return property_binding!.onContext?.(this, destructor) ?? this;
	}

	switchClientStateBinder(new_client_state_binder: PropertiesBinding): ImplementationVisitorContext {
		return this.changeState({ new_client_state_binder: new_client_state_binder });
	}

	changeState(new_state: {
		new_explicit_context?: ImplementationVisitorContext;
		new_client_state_binder?: PropertiesBinding;
		new_binding_context?: any;
		new_model_binding?: api_views.Cmodel_binding;
		new_widget_configuration?: WidgetConfigurationNode;
		new_last_gui_widget_configuration?: api_views.Cgui_widget_configuration_node;
		entry_key?: string | string[];
		new_number_converter?: NumberConverter;
		id_generator?: () => IdGenerator;
	}): ImplementationVisitorContext {
		if (new_state.new_client_state_binder !== undefined && !new_state.new_client_state_binder) {
			throw new Error("client state binder can not be changed to null.");
		}
		if (new_state.new_binding_context !== undefined && !new_state.new_binding_context) {
			throw new Error("new binding context can not be changed to null.");
		}
		const new_last_gui_widget_configuration =
			new_state.new_widget_configuration instanceof api_views.Cgui_widget_configuration_node
				? new_state.new_widget_configuration
				: new_state.new_last_gui_widget_configuration ?? this.last_gui_widget_configuration;
		const new_id_generator =
			new_state.id_generator ||
			(this.id_generator === undefined
				? undefined
				: new_state.new_widget_configuration === undefined
				? this.id_generator
				: undefined);

		return new ImplementationVisitorContext(
			new_state.new_explicit_context ?? this.explicit_context,
			new_state.new_widget_configuration || this.widget_configuration,
			new_state.new_model_binding || this._model_binding,
			new_state.new_client_state_binder || this._client_state_binder,
			new_state.new_binding_context || this._current_binding_context,
			this._parent_widget_implementation_context,
			this._view_root,
			this._client_view,
			this._view_context_node,
			new_state.entry_key !== undefined
				? typeof new_state.entry_key === "string"
					? this.entry_keys.concat(new_state.entry_key)
					: new_state.entry_key
				: this.entry_keys.slice(),
			new_last_gui_widget_configuration,
			new_state.new_number_converter ?? this.number_converter,
			new_id_generator
		);
	}

	then(handler: (context: ImplementationVisitorContext) => ImplementationVisitorContext): ImplementationVisitorContext {
		return handler(this);
	}

	push_widget_implementation_frame(): ImplementationVisitorContext {
		return new ImplementationVisitorContext(
			undefined,
			this.widget_configuration,
			this._model_binding,
			this._client_state_binder,
			this._current_binding_context,
			this.explicit_context,
			this._view_root,
			this._client_view,
			this._view_context_node,
			this._entry_keys.slice(),
			this.last_gui_widget_configuration,
			this.number_converter,
			this.id_generator
		);
	}

	uniqueId(context: api_widget.Cunique_id): string {
		return this.id_generator().generate(context);
	}
}

let next_id = 0;
class IdGenerator {
	private ids = new Map<api_widget.Cunique_id, string>();

	generate(context: api_widget.Cunique_id): string {
		let id = this.ids.get(context);
		if (id === undefined) {
			id = "id:" + next_id;
			next_id += 1;
			this.ids.set(context, id);
		}
		return id;
	}
}

function createIdGenerator(): () => IdGenerator {
	let generator: IdGenerator | undefined;
	return () => {
		if (generator === undefined) {
			generator = new IdGenerator();
		}
		return generator;
	};
}
